import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { hvApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronLeft, CheckCircle2, Lock, X,
  Star, Zap, Link as LinkIcon, BarChart2, FileText, Globe, Palette, Users, Headphones,
} from 'lucide-react';

// Free plan features (what's included)
const FREE_FEATURES = [
  { icon: LinkIcon, label: '1 Bewertungslink', included: true },
  { icon: Star,     label: 'Bewertungs-Funnel', included: true },
  { icon: FileText, label: 'Bewertungsübersicht', included: true },
];

// Upgrade-nudge features (not in free)
const UPGRADE_FEATURES = [
  { icon: Zap,       label: 'KI-Textvorschläge', plan: 'Standard' },
  { icon: Palette,   label: 'Logo & Markenfarben', plan: 'Standard' },
  { icon: Users,     label: 'Gruppen-Segmentierung', plan: 'Standard' },
  { icon: BarChart2, label: 'CSV-Export & Funnel-Statistiken', plan: 'Premium' },
  { icon: Star,      label: 'KI-Sachlichkeitsindex', plan: 'Premium' },
  { icon: Star,      label: 'MetaScore', plan: 'Premium' },
  { icon: Globe,     label: 'Custom Domain', plan: 'Enterprise' },
  { icon: Headphones,label: 'Dedizierter Ansprechpartner', plan: 'Enterprise' },
];

const SIZES = [
  'Ich verwalte privat (1–10 Einheiten)',
  'Kleine Hausverwaltung (10–100 Einheiten)',
  'Mittlere Hausverwaltung (100–500 Einheiten)',
  'Große Hausverwaltung (500+ Einheiten)',
];

const GOALS = [
  'Mehr positive Bewertungen auf Google',
  'Negatives Feedback intern abfangen',
  'Fairen Überblick über die Kundenzufriedenheit',
  'Bewertungsmanagement professionalisieren',
  'Auf Kundenfeedback reagieren können',
];

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 50 : -50, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit:  (dir) => ({ x: dir > 0 ? -50 : 50, opacity: 0 }),
};

function PlanSidebar({ requestedPlan }) {
  const PLAN_LABEL = { standard: 'Standard', premium: 'Premium', enterprise: 'Enterprise' };
  return (
    <div className="hidden lg:flex flex-col w-64 shrink-0 border-l border-border pl-8">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Kostenlos starten</p>
      {requestedPlan && PLAN_LABEL[requestedPlan] && (
        <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
          <span className="font-medium text-primary">{PLAN_LABEL[requestedPlan]}</span> vorgemerkt — Sie starten kostenlos und
          können jederzeit upgraden.
        </div>
      )}

      <div className="mb-5">
        <p className="text-xs text-muted-foreground mb-2">Inklusive:</p>
        <ul className="space-y-2">
          {FREE_FEATURES.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
              <span>{label}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="text-xs text-muted-foreground mb-2">Mit Upgrade verfügbar:</p>
        <ul className="space-y-2">
          {UPGRADE_FEATURES.map(({ icon: Icon, label, plan }) => (
            <li key={label} className="flex items-center gap-2 text-sm text-muted-foreground/60">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 truncate">{label}</span>
              <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-medium shrink-0">{plan}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 p-3 rounded-lg bg-primary/5 border border-primary/20">
        <p className="text-xs text-muted-foreground mb-2">
          <span className="font-semibold text-foreground">Immoverita</span> — faires Bewertungssystem für professionelle Hausverwaltungen.
        </p>
        <p className="text-xs text-muted-foreground">Kostenlos · Keine Kreditkarte erforderlich</p>
      </div>
    </div>
  );
}

export default function OnboardingPage({ overlay = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);

  const STASH_KEY = 'immo_onboarding_draft';
  const [searchParams] = useSearchParams();
  const requestedPlan = searchParams.get('plan') || (() => { try { return localStorage.getItem('immo_plan_intent'); } catch { return null; } })();
  const isLoggedIn = (() => { try { return !!localStorage.getItem('hv_auth_token'); } catch { return false; } })();

  const [form, setForm] = useState(() => {
    // Entwurf wiederherstellen, falls der Nutzer vor dem Login schon ausgefüllt hatte
    try {
      const raw = localStorage.getItem(STASH_KEY);
      if (raw) return { firstName: '', lastName: '', company: '', size: '', goals: [], ...JSON.parse(raw) };
    } catch {}
    return { firstName: '', lastName: '', company: '', size: '', goals: [] };
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const next = () => { setDirection(1); setStep((s) => s + 1); };
  const back = () => { setDirection(-1); setStep((s) => s - 1); };

  const toggleGoal = (goal) => {
    setForm((f) => ({
      ...f,
      goals: f.goals.includes(goal)
        ? f.goals.filter((g) => g !== goal)
        : [...f.goals, goal],
    }));
  };

  const canNext1 = form.firstName.trim() && form.lastName.trim() && form.company.trim();
  const canNext2 = !!form.size;
  const canFinish = form.goals.length > 0;

  const handleFinish = async () => {
    // Ohne Login: Entwurf sichern und zum Login — nach dem Magic Link geht es hier weiter.
    if (!isLoggedIn) {
      try { localStorage.setItem(STASH_KEY, JSON.stringify(form)); } catch {}
      if (requestedPlan) { try { localStorage.setItem('immo_plan_intent', requestedPlan); } catch {} }
      toast.info('Fast geschafft — bitte melden Sie sich an, damit wir Ihr Konto anlegen können.');
      navigate('/app/login', { state: { backgroundLocation: { pathname: '/' } } });
      return;
    }
    setSaving(true);
    try {
      await hvApi.completeOnboarding({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        company: form.company.trim(),
        size: form.size,
        goals: form.goals,
        requested_plan: requestedPlan || null,
      });
      try { localStorage.removeItem(STASH_KEY); localStorage.removeItem('immo_plan_intent'); } catch {}
      // user_data aktualisieren, damit Nav/Settings sofort den neuen Namen zeigen
      try {
        const ud = JSON.parse(localStorage.getItem('user_data') || '{}');
        localStorage.setItem('user_data', JSON.stringify({ ...ud, tenant_name: form.company.trim() || ud.tenant_name }));
      } catch {}
      toast.success('Willkommen bei Immoverita!');
      navigate('/app');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Go back or to home
    if (location.state?.backgroundLocation) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const STEP_TITLES = [
    'Wer sind Sie?',
    'Wie groß ist Ihre Hausverwaltung?',
    'Was ist Ihr wichtigstes Ziel?',
  ];

  const wizardContent = (
    <div className="flex gap-8 flex-1">
      {/* Wizard panel */}
      <div className="flex-1 min-w-0">
        <CardHeader className="pb-2 px-0 pt-0">
          <div className="flex items-center justify-between mb-2">
            <CardTitle className="text-xl">{STEP_TITLES[step - 1]}</CardTitle>
            <span className="text-sm text-muted-foreground shrink-0">{step} / 3</span>
          </div>
          <div className="flex gap-1">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-primary' : 'bg-muted'}`}
              />
            ))}
          </div>
        </CardHeader>

        <CardContent className="px-0 pt-6">
          <AnimatePresence mode="wait" custom={direction}>
            {step === 1 && (
              <motion.div
                key="step1"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.18 }}
                className="space-y-4"
              >
                <div>
                  <Label htmlFor="firstName">Vorname *</Label>
                  <Input id="firstName" className="mt-1" placeholder="Max" value={form.firstName} onChange={set('firstName')} />
                </div>
                <div>
                  <Label htmlFor="lastName">Nachname *</Label>
                  <Input id="lastName" className="mt-1" placeholder="Mustermann" value={form.lastName} onChange={set('lastName')} />
                </div>
                <div>
                  <Label htmlFor="company">Name der Hausverwaltung *</Label>
                  <Input id="company" className="mt-1" placeholder="Muster Hausverwaltung GmbH" value={form.company} onChange={set('company')} />
                </div>
                <Button className="w-full mt-2" onClick={next} disabled={!canNext1}>
                  Weiter <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.18 }}
                className="space-y-3"
              >
                <p className="text-sm text-muted-foreground">Bitte wählen Sie die passende Option:</p>
                {SIZES.map((size) => (
                  <button
                    key={size}
                    onClick={() => setForm((f) => ({ ...f, size }))}
                    className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                      form.size === size ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    {size}
                  </button>
                ))}
                <div className="flex gap-2 pt-2">
                  <Button variant="ghost" onClick={back} className="flex-1">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                  </Button>
                  <Button className="flex-1" onClick={next} disabled={!canNext2}>
                    Weiter <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.18 }}
                className="space-y-3"
              >
                <p className="text-sm text-muted-foreground">Mehrfachauswahl möglich:</p>
                {GOALS.map((goal) => (
                  <button
                    key={goal}
                    onClick={() => toggleGoal(goal)}
                    className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors flex items-center justify-between gap-2 ${
                      form.goals.includes(goal) ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <span>{goal}</span>
                    {form.goals.includes(goal) && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
                  </button>
                ))}

                {(form.firstName || form.size) && (
                  <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground space-y-1">
                    {form.firstName && <p><span className="font-medium">Name:</span> {form.firstName} {form.lastName}</p>}
                    {form.company && <p><span className="font-medium">Hausverwaltung:</span> {form.company}</p>}
                    {form.size && <p><span className="font-medium">Größe:</span> {form.size}</p>}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button variant="ghost" onClick={back} className="flex-1">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                  </Button>
                  <Button className="flex-1" onClick={handleFinish} disabled={!canFinish || saving}>
                    Jetzt loslegen <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </div>

      <PlanSidebar requestedPlan={requestedPlan} />
    </div>
  );

  // ── Overlay mode (shown over landing page) ──────────────────────────────────
  if (overlay) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-background/50 backdrop-blur-md" onClick={handleClose} />
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-background/80 hover:bg-background border border-border text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="relative z-10 w-full max-w-2xl">
          <Card className="shadow-2xl">
            <CardContent className="pt-8 pb-8 px-8">
              <div className="flex items-center justify-between mb-6">
                <img src="/logo.png" alt="Immoverita" className="h-12 w-auto" />
                <span className="text-xs text-muted-foreground">Kostenlos · Keine Kreditkarte</span>
              </div>
              {wizardContent}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── Standalone page (redirect from AuthVerify) ──────────────────────────────
  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      <header className="bg-background border-b border-border px-5 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Immoverita" className="h-14 w-auto" />
          </Link>
          <span className="text-xs text-muted-foreground">Kostenlos · Keine Kreditkarte erforderlich</span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-3xl">
          <Card>
            <CardContent className="pt-8 pb-8 px-8">
              {wizardContent}
            </CardContent>
          </Card>
        </div>
      </div>

      <footer className="bg-background border-t border-border py-4 text-center">
        <p className="text-xs text-muted-foreground">
          <Link to="/" className="hover:underline">Immoverita</Link> · ISQI GmbH
        </p>
      </footer>
    </div>
  );
}
